import { Card, CardContent } from '../../ui';
import { SIGNALS, SIGNAL_META, URGENT_SIGNALS, signalMeta } from '../../../constants/sentiment';
import { cn } from '../../../utils/cn';

/**
 * Actionable signals.
 *
 * Everything else on this page answers "how does the room feel". This answers
 * "is anything on fire", which is a different question and the one a manager
 * opens the product to ask on a bad morning. A legal threat has to be visible
 * without reading a single reply, so it gets a row of its own rather than
 * being one label inside the evidence drawer.
 *
 * Ordered by urgency, not by count: one legal threat outranks forty
 * accusations, and a manager scanning top to bottom must never have to notice
 * that the biggest number is not the worst news. Backing sits at the end,
 * separated, because the defence is worth seeing but is not a thing to act on.
 *
 * Counts come from the run's own totals, which the backend snapshots at the
 * terminal transition, so this never disagrees with the numbers beside it.
 */
export function FlaggedLane({ totals, onOpen, className }) {
	const counts = totals?.signals || {};
	const present = SIGNALS.filter((g) => (counts[g] || 0) > 0);
	if (!present.length) return null;

	const urgent = present.filter((g) => URGENT_SIGNALS.includes(g));
	const context = present.filter((g) => !URGENT_SIGNALS.includes(g));
	const total = present.reduce((n, g) => n + (counts[g] || 0), 0);

	const Row = ({ g }) => {
		const m = signalMeta(g);
		const n = counts[g] || 0;
		// min-w-0 on the row: a grid item defaults to min-width:auto, which
		// refuses to shrink below its content and pushed the count and the
		// action off the card on a phone. The truncate below cannot bite
		// without it.
		return (
			<button
				type="button"
				onClick={() => onOpen?.({ signal: g, title: m.label, text: m.hint })}
				className="flex w-full min-w-0 cursor-pointer items-center gap-3 rounded-lg border border-border px-3 py-2 text-left transition-colors hover:border-border hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
			>
				<span className={cn('h-2 w-2 shrink-0 rounded-full', m.dot)} />
				<span className="min-w-0 flex-1">
					<span className="block text-sm font-medium text-foreground">{m.label}</span>
					<span className="block truncate text-xs text-muted-foreground">{m.hint}</span>
				</span>
				<span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">{n}</span>
				<span className="shrink-0 text-xs text-brand-600">Read</span>
			</button>
		);
	};

	return (
		<Card className={className}>
			<CardContent className="p-4 sm:p-5">
				<div className="flex flex-wrap items-baseline justify-between gap-2">
					<h3 className="text-base font-semibold text-foreground">Actionable signals</h3>
					<button
						type="button"
						onClick={() => onOpen?.({ signal: 'any', title: 'Everything flagged', text: 'Replies that carry a legal threat, an accusation, a boycott call, press interest, a false claim, a fake account, or public backing.' })}
						className="text-xs font-medium text-brand-600 hover:underline"
					>
						See all {total}
					</button>
				</div>
				<p className="mt-0.5 text-sm text-muted-foreground">
					{urgent.length
						? 'Replies that need a person to look at them, not just a number.'
						: 'Nothing urgent this pulse. These are worth knowing about.'}
				</p>

				<div className="mt-3 grid gap-1.5">
					{urgent.map((g) => <Row key={g} g={g} />)}
				</div>

				{context.length > 0 && (
					<div className={cn('grid gap-1.5', urgent.length && 'mt-3 border-t border-border pt-3')}>
						{context.map((g) => <Row key={g} g={g} />)}
					</div>
				)}
			</CardContent>
		</Card>
	);
}

export { SIGNAL_META };
